import { PENDING_CUTOVER } from './pendingCutover'
import type { LegacyIngressDefinition } from './types'

import { CREATIVE_AI_DETAIL_ROUTE, CREATIVE_AI_ROUTE } from '@/features/creative-ai'
import { IMPACT_ANALYSIS_DETAIL_ROUTE, IMPACT_ANALYSIS_ROUTE } from '@/features/impact-analysis'
import { INSIGHTS_PREVIEW_ROUTE } from '@/features/insights'
import { MCP_CONNECT_ROUTE } from '@/features/integrations'
import { COMPETITOR_ANALYSIS_ROUTE, TREND_SEASONALITY_ROUTE } from '@/features/marketing-insights'
import { ORPHEUS_ROUTE } from '@/features/orpheus'
import { registeredReportPages } from '@/features/reports'

const V4_INGRESSES: readonly LegacyIngressDefinition[] = [
  {
    id: 'summary',
    legacyName: 'Summary',
    path: '/summary',
    outcome: { kind: 'v4', target: { name: 'Summary' } },
  },
  {
    id: 'weekly-workflow-result',
    legacyName: 'WeeklySummaryLatest',
    path: '/notifications/summary/weekly/:legacyPath(.*)*',
    outcome: { kind: 'v4', target: { name: 'WeeklySummaryLatest' } },
    preserveHash: true,
  },
  {
    id: 'ai-workflows',
    legacyName: 'AIWorkflows',
    path: '/ai-workflows',
    outcome: { kind: 'v4', target: { name: 'WorkflowCreate' } },
  },
  {
    id: 'orpheus',
    legacyName: 'Orpheus',
    path: '/orpheus',
    outcome: { kind: 'v4', target: ORPHEUS_ROUTE },
    preserveQuery: ['conversation_id'],
  },
  {
    id: 'anomalies',
    legacyName: 'Anomalies',
    path: '/anomalies',
    outcome: { kind: 'v4', target: { name: 'Anomalies' } },
  },
  {
    id: 'health-checks',
    legacyName: 'HealthChecks',
    path: '/tools/health-checks',
    outcome: { kind: 'v4', target: { name: 'HealthChecks' } },
  },
  {
    // ORP-20040. Was the registry's ONLY `decisionRequired` record (owner module-16, ORP-18746);
    // ORP-20033 buckets this surface as A — it must work in v4 before v3 shuts down — and
    // ORP-18753 shipped the route, so the honest outcome is now the v4 target. v3's path and v4's
    // are the same string, so `/data-export` is matched by the real record under the '/' parent
    // long before this one; it is kept for the same reason `health-checks` above is — the registry
    // is the audited inventory of every legacy URL, not just the unresolved ones.
    id: 'data-export',
    legacyName: 'DataExport',
    path: '/data-export',
    outcome: { kind: 'v4', target: { name: 'DataExport' } },
  },
  {
    // ORP-20404, and the same shape as `data-export` above: the v4 Insights Center moved off
    // `insights-v4` onto v3's own `/insights`, so the honest outcome is the v4 target and the
    // generated hand-off route disappears with it. It was the last `pending` row whose owner
    // (ORP-18745) died with the strangler programme — under the single-switch decision (ledger
    // B21) there is no per-route flip left for a successor to own. The row stays because the
    // registry is the audited inventory of every legacy URL, not just the unresolved ones.
    id: 'insights',
    legacyName: 'Insights',
    path: '/insights',
    outcome: { kind: 'v4', target: INSIGHTS_PREVIEW_ROUTE },
    preserveQuery: ['platform', 'search'],
    preserveHash: true,
  },
  {
    id: 'platform-actions',
    legacyName: 'PlatformActions',
    path: '/tools/platform-actions',
    outcome: { kind: 'v4', target: { name: 'PlatformActions' } },
  },
  {
    id: 'impact-analysis',
    legacyName: 'ImpactAnalysis',
    path: '/impact-analysis',
    outcome: { kind: 'v4', target: IMPACT_ANALYSIS_ROUTE },
  },
  {
    // ORP-18719 cutover, results/detail half: the setup wizard + results view landed on the real
    // route below (`impactRoutes`), so this legacy detail URL now resolves there instead of a
    // hard navigation back to v3.
    id: 'impact-detail',
    legacyName: 'ImpactAnalysisDetail',
    path: '/impact-analysis/:executionId',
    outcome: { kind: 'v4', target: IMPACT_ANALYSIS_DETAIL_ROUTE },
    preserveQuery: ['tab'],
  },
  {
    id: 'creative-ai',
    legacyName: 'CreativeAi',
    path: '/creative-ai',
    outcome: { kind: 'v4', target: CREATIVE_AI_ROUTE },
  },
  {
    // The detail page (path/name a 1:1 match with v3's `creative-ai/:analysisId/:tab?`) landed
    // alongside the landing target, so this legacy detail URL now resolves there directly instead
    // of the hard navigation back to v3 that ORP-18906 was tracking.
    id: 'creative-ai-detail',
    legacyName: 'CreativeAiDetail',
    path: '/creative-ai/:analysisId/:tab?',
    outcome: { kind: 'v4', target: CREATIVE_AI_DETAIL_ROUTE },
    preserveQuery: ['media_type'],
    preserveHash: true,
  },
  {
    id: 'kpi-tracking',
    legacyName: 'KPITracking',
    path: '/kpi-tracking',
    outcome: { kind: 'v4', target: { name: 'KPITracking' } },
    preserveQuery: ['report_id'],
  },
  {
    id: 'app-acquisition',
    legacyName: 'MobileAppAcquisition',
    path: '/app-analytics/app-acquisition',
    outcome: { kind: 'v4', target: { name: 'MobileAppAcquisition' } },
  },
  {
    id: 'apple-search-ads',
    legacyName: 'AppleSearchAds',
    path: '/app-analytics/apple-search-ads',
    outcome: { kind: 'v4', target: { name: 'AppleSearchAds' } },
  },
  {
    id: 'product-insights',
    legacyName: 'ProductInsights',
    path: '/ecommerce/product-insights',
    outcome: { kind: 'v4', target: { name: 'ProductInsights' } },
  },
  {
    id: 'performance-overview',
    legacyName: 'PerformanceOverview',
    path: '/budget-performance/performance-overview',
    outcome: { kind: 'v4', target: { name: 'PerformanceOverview' } },
    preserveQuery: ['view'],
  },
  {
    id: 'creative-watchlist',
    legacyName: 'CreativeWatchlist',
    path: '/creative-watchlist',
    outcome: { kind: 'v4', target: { name: 'CreativeWatchlist' } },
    preserveQuery: ['platform', 'tab'],
  },
  {
    id: 'benchmark',
    legacyName: 'Benchmark',
    path: '/benchmark',
    outcome: { kind: 'v4', target: { name: 'Benchmark' } },
    preserveQuery: ['platform'],
  },
  {
    id: 'customer-intelligence',
    legacyName: 'CustomerIntelligence',
    path: '/marketing-insights/customer-intelligence',
    outcome: { kind: 'v4', target: { name: 'CustomerIntelligence' } },
    preserveQuery: ['report_id', 'tab', 'view'],
    preserveHash: true,
  },
  {
    id: 'trend-seasonality-analysis',
    legacyName: 'TrendSeasonalityAnalysis',
    path: '/marketing-insights/trending-competition/trend-seasonality-analysis',
    outcome: { kind: 'v4', target: TREND_SEASONALITY_ROUTE },
    preserveQuery: ['report_id'],
    preserveHash: true,
  },
  {
    id: 'competitor-analysis',
    legacyName: 'CompetitorAnalysis',
    path: '/marketing-insights/trending-competition/competitor-analysis',
    outcome: { kind: 'v4', target: COMPETITOR_ANALYSIS_ROUTE },
    preserveQuery: ['platform'],
    preserveHash: true,
  },
  {
    id: 'workspace-settings',
    legacyName: 'WorkspaceSettings',
    path: '/settings',
    outcome: { kind: 'v4', target: { name: 'WorkspaceSettings' } },
  },
  {
    id: 'get-started',
    legacyName: 'GetStarted',
    path: '/get-started',
    outcome: { kind: 'v4', target: { name: 'GetStarted' } },
  },
  {
    id: 'compare-plans',
    legacyName: 'ComparePlans',
    path: '/compare-plans',
    outcome: { kind: 'v4', target: { name: 'ComparePlans' } },
  },
  {
    id: 'set-plan',
    legacyName: 'SetPlan',
    path: '/set-plan',
    outcome: { kind: 'v4', target: { name: 'SetPlan' } },
  },
  {
    id: 'mcp-connect',
    legacyName: 'McpConnect',
    path: '/settings/mcp/connect',
    outcome: { kind: 'v4', target: MCP_CONNECT_ROUTE },
    preserveQuery: ['assistant', 'session_token', 'state'],
  },
  {
    // ORP-27468: the v4 record of the same name only redirects home now — the connections screen
    // lives on mcp.orphex.co. Kept so a held link resolves instead of falling to the catch-all.
    id: 'mcp-connections',
    legacyName: 'McpConnections',
    path: '/settings/mcp/connections',
    outcome: { kind: 'v4', target: { name: 'McpConnections' } },
  },
  {
    id: 'legacy-workspace-settings',
    legacyName: 'LegacyWorkspaceSettings',
    path: '/workspace-settings/:legacyPath(.*)*',
    outcome: { kind: 'v4', target: { name: 'legacy-workspace-settings' } },
    preserveQuery: ['selected_tab'],
  },
  {
    id: 'custom-dashboard-create',
    legacyName: 'CustomDashboardCreate',
    path: '/custom-dashboard/create-new',
    outcome: { kind: 'v4', target: { name: 'CustomDashboardCreate' } },
    preserveQuery: ['duplicate_dashboard_id'],
  },
  {
    id: 'custom-dashboard-legacy',
    legacyName: 'CustomDashboardLegacy',
    path: '/custom-dashboard/:url?',
    outcome: { kind: 'v4', target: { name: 'CustomDashboardLegacy' } },
    preserveQuery: ['duplicate_dashboard_id'],
  },
  {
    id: 'account-center',
    legacyName: 'AccountCenter',
    path: '/account-center/:url?',
    outcome: { kind: 'v4', target: { name: 'AccountCenter' } },
    preserveQuery: ['duplicate_account_center_id'],
  },
  {
    id: 'user-behavior',
    legacyName: 'UserBehavior',
    path: '/audience/user-behavior',
    outcome: { kind: 'v4', target: { name: 'UserBehavior' } },
    preserveQuery: ['report_id'],
    preserveHash: true,
  },
  {
    id: 'custom-dashboard',
    legacyName: 'CustomDashboard',
    path: String.raw`/:account_id(\d+)/:workspace_id(\d+)/:url`,
    outcome: { kind: 'v4', target: { name: 'CustomDashboard' } },
    preserveQuery: ['duplicate_dashboard_id'],
  },
  ...registeredReportPages().map((page): LegacyIngressDefinition => ({
    id: `report-${page.navKey}`,
    legacyName: page.routeName,
    path: `/${page.path}`,
    outcome: { kind: 'v4', target: { name: page.routeName } },
    preserveQuery: ['report_id', 'tab', 'view'],
    preserveHash: true,
  })),
]

const RETIRED: readonly LegacyIngressDefinition[] = [
  {
    // Not a retirement we invented: the feature shipped in v4 at a different URL. v3's
    // `/credit-settings` became `/settings/credits` (SettingsCredits), so the legacy path has a
    // real destination and the outcome view can offer it as a one-click replacement.
    id: 'credit-settings',
    legacyName: 'CreditSettings',
    path: '/credit-settings',
    outcome: {
      kind: 'retired',
      replacement: { name: 'SettingsCredits' },
      messageKey: 'shell.ingress.retired.description',
    },
  },
]

/**
 * EMPTY since ORP-20040 — every audited legacy URL now has a real outcome. The array (and the
 * `decisionRequired` outcome kind) stays as the declared home for the next surface whose product
 * decision is genuinely open; `unresolvedLegacyIngresses()` is the guard that keeps it honest.
 */
const DECISION_REQUIRED: readonly LegacyIngressDefinition[] = []

export const LEGACY_INGRESSES: readonly LegacyIngressDefinition[] = Object.freeze([
  ...V4_INGRESSES,
  ...PENDING_CUTOVER,
  ...RETIRED,
  ...DECISION_REQUIRED,
])

const byId = new Map(LEGACY_INGRESSES.map((definition) => [definition.id, definition]))

export function legacyIngressById(id: string): LegacyIngressDefinition | null {
  return byId.get(id) ?? null
}

export function unresolvedLegacyIngresses(): readonly LegacyIngressDefinition[] {
  return LEGACY_INGRESSES.filter((definition) => definition.outcome.kind === 'decisionRequired')
}
